import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import {
  Save, Loader2, Sparkles, MessageCircle, Send, RotateCw, Bot, User as UserIcon,
  AlertCircle, BookText, Wand2, ChevronDown
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELD = "block w-full bg-white border border-[#D4859B]/20 focus:border-seikat-pink rounded-xl px-3.5 py-2 text-[#1C1A19] placeholder:text-[#A8A29A] font-body text-sm outline-none transition-colors";
const LABEL = "block text-[10px] tracking-[0.2em] uppercase text-[#8A857D] mb-1.5 font-body";

const PROMPT_PRESETS = [
  {
    name: "Curator Puitis (Default)",
    description: "Hangat, intim, puitis dalam Bahasa Indonesia.",
    prompt:
      "Kamu adalah Memory Curator dari SEIKAT, sebuah atelier bunga premium Indonesia. " +
      "Tugasmu: berbincang singkat dengan customer (3–5 pertanyaan) untuk menggali momen, perasaan, " +
      "dan kepribadian penerima bouquet. " +
      "Gaya bicara: hangat, puitis, intim, seperti sahabat yang mendengarkan dengan tenang. " +
      "Bahasa: utamakan Bahasa Indonesia yang puitis dan lembut, kecuali user pakai English. " +
      "Ajukan SATU pertanyaan terbuka per giliran. Jangan menumpuk pertanyaan. Jangan menyebut nama bunga spesifik. " +
      "Setelah cukup info (siapa penerima, momen, perasaan, mungkin warna kesukaan), berikan ringkasan puitis 1–2 kalimat " +
      "dan akhiri dengan: \"Aku sudah merasakan harum ceritamu. Mari kita rangkai.\" " +
      "Jangan menulis lebih dari 60 kata per respons. Jangan pakai emoji. Hindari kata-kata terlalu formal seperti 'Saudara/i'.",
  },
  {
    name: "Curator Profesional",
    description: "Ramah, fokus, lebih to-the-point untuk pelanggan korporat.",
    prompt:
      "Anda adalah Konsultan Bouquet dari SEIKAT. Tugas: gali dengan efisien (max 4 pertanyaan) untuk memahami " +
      "acara, penerima, dan preferensi visual. Gaya: ramah, profesional, jernih. Tetap hangat tapi tidak terlalu puitis. " +
      "Bahasa: utamakan Bahasa Indonesia formal, switch ke English jika user pakai English. " +
      "Satu pertanyaan terbuka per giliran. Hindari sebut nama bunga teknis. Setelah cukup info, rangkum singkat " +
      "dan akhiri: \"Saya sudah memahami kebutuhan Anda. Mari kita rangkai.\" Maksimal 50 kata per respons.",
  },
  {
    name: "Curator Casual",
    description: "Santai, friendly, cocok untuk audiens Gen-Z.",
    prompt:
      "Hai, kamu adalah Memory Curator SEIKAT yang santai dan friendly. Bantu user mendeskripsikan momen mereka " +
      "lewat percakapan singkat (3–5 putaran). Gaya bicara: warm, casual, sedikit playful tapi tetap respectful. " +
      "Bahasa: Indonesia santai (boleh sedikit gaul tapi jangan berlebihan), atau English casual jika user pakai English. " +
      "Satu pertanyaan per giliran. Hindari nama bunga spesifik. Setelah info cukup, rangkum dan tutup dengan: " +
      "\"Aku udah ngerasain vibe-nya. Yuk kita rangkai.\" Max 60 kata. No emoji.",
  },
];

const MODEL_HINTS = {
  gemini: ["gemini-2.5-flash", "gemini-2.5-pro", "gemini-3.1-pro-preview"],
  openai: ["gpt-4o", "gpt-4o-mini", "gpt-5.2"],
  anthropic: ["claude-sonnet-4-5-20250929", "claude-3-5-sonnet-20241022"],
};

function approxTokens(text) {
  if (!text) return 0;
  // Rough heuristic: 1 token ~= 4 chars (English/Indonesian mix)
  return Math.ceil(text.length / 4);
}

export default function AdminMemory() {
  const [cfg, setCfg] = useState(null);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showPresets, setShowPresets] = useState(false);

  // Test chat state
  const [chatSession, setChatSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [userInput, setUserInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [testLang, setTestLang] = useState("id");
  const messagesEndRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/admin/cms/memory-config`, { withCredentials: true })
      .then(r => { setCfg(r.data); setInitial(JSON.stringify(r.data)); })
      .catch(() => toast.error("Gagal memuat konfigurasi memory"));
  }, []);

  const isDirty = useMemo(() => cfg && initial && JSON.stringify(cfg) !== initial, [cfg, initial]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, chatLoading]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/cms/memory-config`, cfg, { withCredentials: true });
      setInitial(JSON.stringify(cfg));
      toast.success("Memory AI config tersimpan");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Simpan gagal");
    } finally {
      setSaving(false);
    }
  };

  const applyPreset = (preset) => {
    setCfg(prev => ({ ...prev, system_prompt: preset.prompt }));
    setShowPresets(false);
    toast.success(`Preset "${preset.name}" diterapkan`);
  };

  const startChat = async () => {
    if (isDirty) {
      const ok = window.confirm("Ada perubahan yang belum disimpan. Test chat memakai config yang sudah TERSIMPAN. Simpan dulu?");
      if (ok) {
        await save();
      }
    }
    setChatLoading(true);
    try {
      const { data } = await axios.post(`${API}/memory/chat/start`, { language: testLang });
      setChatSession(data.session_id);
      setMessages([{ role: "assistant", content: data.message, ts: new Date().toISOString() }]);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Gagal memulai chat");
    } finally {
      setChatLoading(false);
    }
  };

  const sendMessage = async () => {
    const text = userInput.trim();
    if (!text || !chatSession || chatLoading) return;
    setUserInput("");
    setMessages(prev => [...prev, { role: "user", content: text, ts: new Date().toISOString() }]);
    setChatLoading(true);
    try {
      const { data } = await axios.post(`${API}/memory/chat/${chatSession}/message`, { message: text });
      setMessages(prev => [...prev, { role: "assistant", content: data.message, ts: new Date().toISOString(), is_ready: data.is_ready, can_show_cta: data.can_show_cta }]);
    } catch (e) {
      const detail = e.response?.data?.detail || "AI error";
      setMessages(prev => [...prev, { role: "assistant", content: `⚠️ ${detail}`, error: true, ts: new Date().toISOString() }]);
    } finally {
      setChatLoading(false);
    }
  };

  const resetChat = () => {
    setChatSession(null);
    setMessages([]);
    setUserInput("");
  };

  if (!cfg) return <Loader2 className="animate-spin text-seikat-pink mx-auto mt-20" size={28} />;

  const promptTokens = approxTokens(cfg.system_prompt);
  const modelHints = MODEL_HINTS[cfg.model_provider] || [];

  return (
    <div data-testid="admin-memory">
      {/* Header */}
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2"><span className="w-8 h-px bg-seikat-pink" /><span className="text-[10px] tracking-[0.35em] uppercase text-seikat-pink font-body">Memory AI</span></div>
          <h1 className="font-heading text-4xl text-[#1C1A19] leading-tight">AI <em className="text-seikat-pink">Memory Curator</em></h1>
          <p className="font-body text-sm text-[#4A453F]/75 mt-1.5">Atur prompt curator, model, dan test percakapan langsung di sini.</p>
        </div>
        <div className="flex items-center gap-2">
          {isDirty && <span className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-50 border border-amber-200 rounded-full text-[11px] font-body text-amber-700"><AlertCircle size={11} /> Belum disimpan</span>}
          <button onClick={save} disabled={saving || !isDirty} className="flex items-center gap-2 px-5 py-2.5 bg-seikat-pink text-white rounded-full text-sm font-body font-medium hover:bg-seikat-pink/90 transition-colors disabled:opacity-40" data-testid="admin-memory-save">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <Save size={14} />} {saving ? "Menyimpan…" : "Simpan"}
          </button>
        </div>
      </div>

      {/* Main layout: config left, test chat right */}
      <div className="grid lg:grid-cols-2 gap-5">
        {/* CONFIG */}
        <div className="space-y-4">
          <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={13} className="text-seikat-pink" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Intro & CTA</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={LABEL}>🇮🇩 Intro — ID</label><textarea rows={2} className={FIELD} value={cfg.intro_id || ""} onChange={e => setCfg({ ...cfg, intro_id: e.target.value })} /></div>
              <div><label className={LABEL}>🇬🇧 Intro — EN</label><textarea rows={2} className={FIELD} value={cfg.intro_en || ""} onChange={e => setCfg({ ...cfg, intro_en: e.target.value })} /></div>
              <div><label className={LABEL}>🇮🇩 Starter Question — ID</label><textarea rows={2} className={FIELD} value={cfg.starter_question_id || ""} onChange={e => setCfg({ ...cfg, starter_question_id: e.target.value })} data-testid="memory-starter-id" /></div>
              <div><label className={LABEL}>🇬🇧 Starter Question — EN</label><textarea rows={2} className={FIELD} value={cfg.starter_question_en || ""} onChange={e => setCfg({ ...cfg, starter_question_en: e.target.value })} /></div>
              <div><label className={LABEL}>🇮🇩 CTA Label — ID</label><input className={FIELD} value={cfg.cta_label_id || ""} onChange={e => setCfg({ ...cfg, cta_label_id: e.target.value })} /></div>
              <div><label className={LABEL}>🇬🇧 CTA Label — EN</label><input className={FIELD} value={cfg.cta_label_en || ""} onChange={e => setCfg({ ...cfg, cta_label_en: e.target.value })} /></div>
            </div>
          </div>

          <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-4">
              <Bot size={13} className="text-seikat-pink" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">Model & Turns</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><label className={LABEL}>Min turns before CTA</label><input type="number" min="1" max="10" className={FIELD} value={cfg.min_turns_before_cta || 3} onChange={e => setCfg({ ...cfg, min_turns_before_cta: parseInt(e.target.value || "3", 10) })} data-testid="memory-min-turns" /></div>
              <div><label className={LABEL}>Max turns</label><input type="number" min="2" max="15" className={FIELD} value={cfg.max_turns || 6} onChange={e => setCfg({ ...cfg, max_turns: parseInt(e.target.value || "6", 10) })} /></div>
              <div><label className={LABEL}>Model Provider</label>
                <select className={FIELD} value={cfg.model_provider || "gemini"} onChange={e => setCfg({ ...cfg, model_provider: e.target.value })}>
                  <option value="gemini">Gemini</option>
                  <option value="openai">OpenAI</option>
                  <option value="anthropic">Anthropic</option>
                </select>
              </div>
              <div>
                <label className={LABEL}>Model Name</label>
                <input list="model-hints" className={FIELD} value={cfg.model_name || ""} onChange={e => setCfg({ ...cfg, model_name: e.target.value })} data-testid="memory-model-name" />
                <datalist id="model-hints">{modelHints.map(m => <option key={m} value={m} />)}</datalist>
              </div>
            </div>
          </div>

          {/* System Prompt with presets */}
          <div className="bg-white border border-[#D4859B]/15 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <BookText size={13} className="text-seikat-pink" />
                <span className="text-[10px] tracking-[0.3em] uppercase text-[#4A453F] font-body">System Prompt</span>
              </div>
              <div className="relative">
                <button onClick={() => setShowPresets(s => !s)} className="flex items-center gap-1.5 px-3 py-1.5 bg-seikat-pink/10 hover:bg-seikat-pink/20 text-seikat-pink rounded-full text-[11px] font-body" data-testid="prompt-presets-btn">
                  <Wand2 size={11} /> Preset <ChevronDown size={9} />
                </button>
                {showPresets && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-[#D4859B]/20 rounded-xl shadow-xl z-20 min-w-[280px] max-w-sm overflow-hidden">
                    {PROMPT_PRESETS.map(p => (
                      <button key={p.name} onClick={() => applyPreset(p)} className="w-full text-left px-4 py-3 hover:bg-[#FDF8F6] border-b border-[#D4859B]/8 last:border-b-0" data-testid={`preset-${p.name.toLowerCase().replace(/\W+/g, "-")}`}>
                        <p className="font-body text-sm font-medium text-[#1C1A19]">{p.name}</p>
                        <p className="font-body text-[11px] text-[#8A857D] mt-0.5">{p.description}</p>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <textarea rows={10} className={`${FIELD} font-mono text-[11px]`} value={cfg.system_prompt || ""} onChange={e => setCfg({ ...cfg, system_prompt: e.target.value })} data-testid="memory-system-prompt" />
            <div className="flex items-center justify-between mt-2">
              <p className="text-[10px] text-[#8A857D]">Define how AI behaves, tone, response length. Runtime otomatis menambah instruksi bahasa.</p>
              <p className="text-[10px] text-seikat-pink font-medium">~{promptTokens} tokens</p>
            </div>
          </div>
        </div>

        {/* TEST CHAT */}
        <div className="sticky top-4 self-start">
          <div className="bg-gradient-to-b from-[#1C1A19] to-[#2A2422] rounded-2xl overflow-hidden shadow-xl flex flex-col" style={{ height: "calc(100vh - 220px)", minHeight: "500px" }}>
            {/* Chat header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-[#1C1A19]">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-seikat-pink to-[#a85d72] flex items-center justify-center">
                  <Sparkles size={14} className="text-white" />
                </div>
                <div>
                  <p className="font-body text-xs text-white">Memory Curator</p>
                  <p className="text-[10px] text-white/50 font-body">{cfg.model_provider} · {cfg.model_name}</p>
                </div>
              </div>
              <div className="flex items-center gap-1.5">
                <select value={testLang} onChange={e => setTestLang(e.target.value)} className="px-2 py-1 bg-white/10 border border-white/15 rounded-full text-[10px] text-white outline-none" data-testid="test-chat-lang">
                  <option value="id">🇮🇩 ID</option>
                  <option value="en">🇬🇧 EN</option>
                </select>
                {chatSession && (
                  <button onClick={resetChat} className="w-7 h-7 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center" title="Reset" data-testid="test-chat-reset">
                    <RotateCw size={11} />
                  </button>
                )}
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {!chatSession ? (
                <div className="h-full flex flex-col items-center justify-center text-center px-6">
                  <MessageCircle size={32} className="text-seikat-pink/60 mb-3" />
                  <p className="font-heading text-xl text-white mb-1">Test Chat Curator</p>
                  <p className="font-body text-xs text-white/55 mb-5">Mulai percakapan untuk menguji prompt, model, dan tone curator dengan config yang sudah tersimpan.</p>
                  <button onClick={startChat} disabled={chatLoading} className="flex items-center gap-2 px-5 py-2.5 bg-seikat-pink text-white rounded-full text-sm font-body font-medium hover:bg-seikat-pink/90 disabled:opacity-50" data-testid="test-chat-start">
                    {chatLoading ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />}
                    Mulai Test Chat
                  </button>
                </div>
              ) : (
                <>
                  <AnimatePresence initial={false}>
                    {messages.map((m, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 8, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ duration: 0.25 }}
                        className={`flex items-start gap-2 ${m.role === "user" ? "flex-row-reverse" : ""}`}
                      >
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${m.role === "user" ? "bg-white/10" : "bg-gradient-to-br from-seikat-pink to-[#a85d72]"}`}>
                          {m.role === "user" ? <UserIcon size={11} className="text-white/80" /> : <Sparkles size={11} className="text-white" />}
                        </div>
                        <div className={`max-w-[78%] rounded-2xl px-3.5 py-2.5 ${
                          m.role === "user"
                            ? "bg-white/15 text-white"
                            : m.error
                            ? "bg-red-500/15 text-red-200 border border-red-400/30"
                            : "bg-seikat-pink/12 text-white border border-seikat-pink/25"
                        }`}>
                          <p className="font-body text-sm leading-relaxed whitespace-pre-line">{m.content}</p>
                          {m.is_ready && (
                            <p className="text-[10px] tracking-widest uppercase text-seikat-pink mt-2 font-medium">✨ Ready signal terdeteksi</p>
                          )}
                          {m.can_show_cta && !m.is_ready && (
                            <p className="text-[10px] tracking-widest uppercase text-[#C5DB7B]/80 mt-2 font-medium">CTA dapat ditampilkan</p>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  {chatLoading && (
                    <div className="flex items-start gap-2">
                      <div className="w-7 h-7 rounded-full bg-gradient-to-br from-seikat-pink to-[#a85d72] flex items-center justify-center"><Sparkles size={11} className="text-white" /></div>
                      <div className="bg-seikat-pink/12 rounded-2xl px-4 py-3 border border-seikat-pink/25">
                        <div className="flex gap-1">
                          <span className="w-1.5 h-1.5 rounded-full bg-seikat-pink animate-bounce" style={{ animationDelay: "0ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-seikat-pink animate-bounce" style={{ animationDelay: "150ms" }} />
                          <span className="w-1.5 h-1.5 rounded-full bg-seikat-pink animate-bounce" style={{ animationDelay: "300ms" }} />
                        </div>
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input */}
            {chatSession && (
              <div className="p-3 border-t border-white/10 bg-[#1C1A19]">
                <form onSubmit={(e) => { e.preventDefault(); sendMessage(); }} className="flex items-center gap-2">
                  <input
                    value={userInput}
                    onChange={e => setUserInput(e.target.value)}
                    placeholder={testLang === "id" ? "Ketik balasan…" : "Type your reply…"}
                    disabled={chatLoading}
                    className="flex-1 bg-white/10 border border-white/15 focus:border-seikat-pink rounded-full px-4 py-2 text-sm font-body text-white placeholder:text-white/40 outline-none transition-colors"
                    data-testid="test-chat-input"
                  />
                  <button
                    type="submit"
                    disabled={chatLoading || !userInput.trim()}
                    className="w-9 h-9 rounded-full bg-seikat-pink hover:bg-seikat-pink/90 disabled:opacity-40 text-white flex items-center justify-center"
                    data-testid="test-chat-send"
                  >
                    {chatLoading ? <Loader2 className="animate-spin" size={13} /> : <Send size={13} />}
                  </button>
                </form>
                <p className="text-[10px] text-white/35 mt-2 text-center">Test chat memakai config yang sudah TERSIMPAN. Simpan dulu untuk test perubahan baru.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
